import React, { useEffect, useState } from "react";
import {
    Box, Grid, Paper, Typography, CircularProgress,
    Avatar, Chip, Button, Card, CardContent, CardActions, useTheme, useMediaQuery
} from "@mui/material";
//import axios from "axios";
import api from "../components/axios/axiosInstance";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SchoolIcon from "@mui/icons-material/School";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import StarIcon from "@mui/icons-material/Star";

const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Paper
        elevation={2}
        sx={{
            p: { xs: 1.5, sm: 2 },
            height: '100%',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
            }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ bgcolor: color, width: 36, height: 36, mr: 1 }}>{icon}</Avatar>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" fontWeight="bold">{value}</Typography>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
            </Box>
        </Box>
        {subtitle && (
            <Chip
                label={subtitle}
                size="small"
                color="success"
                sx={{ fontSize: '0.7rem', height: 24, mt: 1 }}
            />
        )}
    </Paper>
);

const activityIcons = {
    student_added: <PeopleAltIcon />,
    dok_created: <AssessmentIcon />,
    dok_updated: <AssessmentIcon />,
    dok_deleted: <AssessmentIcon />,
    subject_updated: <SchoolIcon />,
    unit_updated: <SchoolIcon />,
    summary_generated: <TrendingUpIcon />,
    summary_saved: <TrendingUpIcon />,
    skills_generated: <StarIcon />,
    deleted: <StarIcon />,
};

const activityLabels = {
    student_added: "Student Added",
    dok_created: "DOK Report Created",
    dok_updated: "DOK Report Updated",
    dok_deleted: "DOK Report Deleted",
    subject_updated: "Subject Updated",
    unit_updated: "Unit Updated",
    summary_generated: "Summary Generated",
    summary_saved: "Summary Saved",
    skills_generated: "Skills Generated",
    deleted: "Deleted Item",
};

const TeacherDashboardLanding = ({ setActiveTab, setActiveTabGlobal, user }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        total_students: 0,
        total_subjects: 0,
        total_units: 0,
        progress_cards_created: 0,
        ai_summaries_generated: { subject_level: 0 }
    });
    const [activityLog, setActivityLog] = useState([]);



    useEffect(() => {
        api
            .get(`${process.env.REACT_APP_BACKEND_URL}/grades/teacher-dashboard/summary/`)
            .then((res) => setSummary(res.data))
            .catch((err) => {
                // Error handled silently
            })
            .finally(() => setLoading(false));

        api
            .get(`${process.env.REACT_APP_BACKEND_URL}/grades/teacher-dashboard/recent-activity/`)
            .then((res) => setActivityLog(res.data?.slice(0, 4) || []))
            .catch((err) => {
                // Error handled silently
            });
    }, []);

    const {
        total_students,
        total_subjects,
        total_units,
        progress_cards_created,
        ai_summaries_generated: { subject_level }
    } = summary;

    const dok_total = total_students * total_subjects * total_units || 1;
    const dok_remaining = Math.max(dok_total - progress_cards_created, 0);
    const dok_completed = Math.min(progress_cards_created, dok_total);

    const summary_total = total_students * total_subjects || 1;
    const summary_remaining = Math.max(summary_total - subject_level, 0);
    const summary_completed = Math.min(subject_level, summary_total);

    const quickActions = [
        {
            title: "Add New Student",
            description: "Register a new student in your class",
            icon: <PeopleAltIcon />,
            color: theme.palette.primary.main,
            action: "Add Student",
            tab: "Manage Students"
        },
        {
            title: "Create DOK Report",
            description: "Generate assessment report for students",
            icon: <AssessmentIcon />,
            color: theme.palette.secondary.main,
            action: "Create Report",
            tab: "Manage Student Progress"
        },
        {
            title: "Manage Subjects",
            description: "Update curriculum and learning units",
            icon: <SchoolIcon />,
            color: theme.palette.success.main,
            action: "Manage",
            tab: "Manage Subjects & Units"
        }
    ];

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', mx: 'auto' }}>
            <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    color="primary"
                    gutterBottom
                >
                    Welcome back! {user?.display_name} 👋
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Here's what's happening with your classes today
                </Typography>
            </Box>

            {loading ? (
                <Box textAlign="center" mt={5}><CircularProgress /></Box>
            ) : (
                <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={6} md={3}><StatCard title="Total Students" value={total_students} icon={<PeopleAltIcon />} color={theme.palette.primary.main} /></Grid>
                        <Grid item xs={6} md={3}><StatCard title="Total Subjects" value={total_subjects} icon={<SchoolIcon />} color={theme.palette.secondary.main} /></Grid>
                        <Grid item xs={6} md={3}><StatCard title="Total Units" value={total_units} icon={<SchoolIcon />} color={theme.palette.success.main} /></Grid>
                        {/*<Grid item xs={6} md={3}><StatCard title="Total DOK-assessments required" value={progress_cards_created} icon={<TrendingUpIcon />} color={theme.palette.info.main} /></Grid> */}
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <StatCard
                                title="DOK-assessments recorded"
                                value={`${dok_completed} / ${dok_total}`}
                                icon={<AssessmentIcon />}
                                color={theme.palette.primary.main}
                                subtitle={`Remaining: ${dok_remaining}`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <StatCard
                                title="Subject Summaries Completed"
                                value={`${summary_completed} / ${summary_total}`}
                                icon={<AssessmentIcon />}
                                color={theme.palette.secondary.main}
                                subtitle={`Remaining: ${summary_remaining}`}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={4}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Quick Actions</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {quickActions.map((action, index) => (
                                        <Card key={index} variant="outlined" sx={{ borderRadius: 1, '&:hover': { boxShadow: 2, transform: 'translateX(4px)' }, transition: 'all 0.2s ease-in-out' }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Avatar sx={{ bgcolor: action.color, width: 36, height: 36, mr: 1.5 }}>{action.icon}</Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold">{action.title}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{action.description}</Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                            <CardActions sx={{ p: 1, pt: 0 }}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    sx={{ fontSize: '0.75rem' }}
                                                    onClick={() => {
                                                        if (setActiveTab) setActiveTab(action.tab);
                                                        if (setActiveTabGlobal) setActiveTabGlobal(action.tab);
                                                    }}
                                                >
                                                    {action.action}
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} lg={8}>
                            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" fontWeight="bold">Recent Activity</Typography>
                                    <Chip icon={<NotificationsIcon />} label="View All" size="small" variant="outlined" clickable />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {activityLog.map((activity, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 1, bgcolor: 'background.default', '&:hover': { bgcolor: 'action.hover' } }}>
                                            <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: theme.palette.success.main }}>
                                                {activityIcons[activity.type] || <StarIcon />}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                    {activityLabels[activity.type] || activity.type}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    {activity.message}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(activity.timestamp).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Chip label={activity.status} size="small" color="success" sx={{ fontSize: '0.7rem', height: 24 }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default TeacherDashboardLanding;
